/**
 * Permission help-text helper.
 *
 * When the user denies an OS permission prompt (notifications, location, mic,
 * etc.) the in-app fallback alert needs to give them an accurate path to fix
 * it. The path differs per platform:
 *
 *   - Native iOS / Android → "Settings" app (deep-linkable via Linking.openSettings()).
 *   - Electron (macOS / Win / Linux) → host-OS System Settings under specific panes.
 *   - Pure web browser → site permissions UI behind the address-bar padlock.
 *
 * Hard-coding "Allow … in your browser settings (the lock icon in the
 * address bar)" copy in alerts that ALSO run inside Electron has shipped a
 * confused UX twice now (notifications + location), because the Electron
 * window has no address bar. Centralising the copy here keeps every future
 * permission-denied alert from regressing into the same bug.
 *
 * Returns a single sentence ready to drop into Alert.alert's body — no
 * leading capitalisation work needed by the caller.
 */
import { Linking, Platform } from 'react-native';
import { isElectron, getElectronBridge } from './platform';

export type PermissionFeature =
  | 'notifications'
  | 'location'
  | 'microphone'
  | 'camera'
  | 'contacts';

type ElectronOS = 'darwin' | 'win32' | 'linux';

// Deep-link URLs that open the OS-level privacy pane scoped to a single
// feature. macOS uses the documented `x-apple.systempreferences:` scheme;
// Windows uses the modern Settings app's `ms-settings:` URI. Linux has no
// universal equivalent, so we let the caller fall back to the help text.
const ELECTRON_SETTINGS_URLS: Record<ElectronOS, Record<PermissionFeature, string | null>> = {
  darwin: {
    notifications: 'x-apple.systempreferences:com.apple.preference.notifications',
    location:      'x-apple.systempreferences:com.apple.preference.security?Privacy_LocationServices',
    microphone:    'x-apple.systempreferences:com.apple.preference.security?Privacy_Microphone',
    camera:        'x-apple.systempreferences:com.apple.preference.security?Privacy_Camera',
    contacts:      'x-apple.systempreferences:com.apple.preference.security?Privacy_Contacts',
  },
  win32: {
    notifications: 'ms-settings:notifications',
    location:      'ms-settings:privacy-location',
    microphone:    'ms-settings:privacy-microphone',
    camera:        'ms-settings:privacy-webcam',
    contacts:      'ms-settings:privacy-contacts',
  },
  linux: {
    notifications: null,
    location:      null,
    microphone:    null,
    camera:        null,
    contacts:      null,
  },
};

// Settings paths verbatim (use the user-visible names from each OS so the
// copy matches what the user is about to read on their screen).
const ELECTRON_PATHS: Record<ElectronOS, Record<PermissionFeature, string>> = {
  darwin: {
    notifications: 'System Settings → Notifications → Eleplan',
    location:      'System Settings → Privacy & Security → Location Services → Eleplan',
    microphone:    'System Settings → Privacy & Security → Microphone → Eleplan',
    camera:        'System Settings → Privacy & Security → Camera → Eleplan',
    contacts:      'System Settings → Privacy & Security → Contacts → Eleplan',
  },
  win32: {
    notifications: 'Settings → System → Notifications → Eleplan',
    location:      'Settings → Privacy & security → Location → Eleplan',
    microphone:    'Settings → Privacy & security → Microphone → Eleplan',
    camera:        'Settings → Privacy & security → Camera → Eleplan',
    contacts:      'Settings → Privacy & security → Contacts → Eleplan',
  },
  linux: {
    notifications: 'your system notification settings',
    location:      'your system location settings',
    microphone:    'your system microphone settings',
    camera:        'your system camera settings',
    contacts:      'your system contacts settings',
  },
};

const FEATURE_LABEL: Record<PermissionFeature, string> = {
  notifications: 'notifications',
  location:      'location access',
  microphone:    'microphone access',
  camera:        'camera access',
  contacts:      'contacts access',
};

/**
 * Returns a sentence describing how to enable the given permission for
 * Eleplan on the current platform. Caller is responsible for prepending
 * any context (e.g. "We couldn't enable notifications. ").
 */
export function permissionHelpText(feature: PermissionFeature): string {
  const label = FEATURE_LABEL[feature];

  if (isElectron) {
    const os = (getElectronBridge()?.platform as ElectronOS | undefined) ?? 'darwin';
    const path = ELECTRON_PATHS[os][feature];
    return `Allow ${label} in ${path}, then try again.`;
  }

  if (Platform.OS === 'web') {
    return `Allow ${label} for this site from your browser settings (the lock icon in the address bar) and try again.`;
  }

  // Native iOS / Android: caller typically pairs this with a button that
  // calls Linking.openSettings(), which deep-links into the per-app
  // permissions screen.
  return `Please enable ${label} in your device settings.`;
}

/**
 * True when the current platform can deep-link into a per-feature OS
 * settings pane. Electron (macOS/Windows) and native iOS/Android can.
 * Pure web and Electron-on-Linux can't — there's nowhere programmatic to go.
 */
export function canOpenSystemSettings(feature: PermissionFeature): boolean {
  if (isElectron) {
    const os = (getElectronBridge()?.platform as ElectronOS | undefined) ?? 'darwin';
    return !!ELECTRON_SETTINGS_URLS[os][feature];
  }
  return Platform.OS === 'ios' || Platform.OS === 'android';
}

/**
 * Opens the OS-level privacy pane for the given feature on the platforms
 * that support it. No-op on pure web (browsers don't expose programmatic
 * access to the address-bar permissions UI) and on Electron-on-Linux.
 */
export async function openSystemSettingsFor(feature: PermissionFeature): Promise<void> {
  if (isElectron) {
    const bridge = getElectronBridge();
    const os = (bridge?.platform as ElectronOS | undefined) ?? 'darwin';
    const url = ELECTRON_SETTINGS_URLS[os][feature];
    if (bridge && url) await bridge.openExternal(url);
    return;
  }
  if (Platform.OS === 'ios' || Platform.OS === 'android') {
    Linking.openSettings();
  }
}

/**
 * Buttons for a permission-denied alert: native iOS/Android and Electron can
 * deep-link into the settings pane; pure web has nowhere to go, so just OK.
 * Shared so every screen that asks for a permission offers the same way out.
 */
export function buildPermissionAlertButtons(feature: PermissionFeature) {
  if (!canOpenSystemSettings(feature)) {
    return [{ text: 'OK' }];
  }
  return [
    { text: 'Cancel', style: 'cancel' as const },
    { text: 'Open Settings', onPress: () => { void openSystemSettingsFor(feature); } },
  ];
}
